import { useEffect, useRef, useState } from 'react';
import { Download } from 'lucide-react';

/**
 * "Export your data" control: fetches the authenticated `GET /export` zip,
 * saves the blob, and reports progress through a polite live region. Extracted
 * from SideNav so the drawer, the desktop sidebar, and Profile can share one
 * implementation.
 */
export function ExportButton() {
  const [exportStatus, setExportStatus] = useState<'idle' | 'loading' | 'success' | 'error'>(
    'idle'
  );
  const resetTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    return () => {
      if (resetTimerRef.current) clearTimeout(resetTimerRef.current);
    };
  }, []);

  const handleExport = async () => {
    if (exportStatus === 'loading') return;
    if (resetTimerRef.current) {
      clearTimeout(resetTimerRef.current);
      resetTimerRef.current = null;
    }
    setExportStatus('loading');
    try {
      const res = await fetch('/export', { credentials: 'include' });
      if (!res.ok) throw new Error(`Export failed with status ${res.status}`);
      const blob = await res.blob();

      const disposition = res.headers.get('content-disposition');
      const match = disposition?.match(/filename="([^"]+)"/);
      let filename = match?.[1];
      if (!filename) {
        const now = new Date();
        const yyyy = now.getFullYear();
        const mm = String(now.getMonth() + 1).padStart(2, '0');
        const dd = String(now.getDate()).padStart(2, '0');
        filename = `alma-export-${yyyy}-${mm}-${dd}.zip`;
      }

      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      a.click();
      URL.revokeObjectURL(url);

      setExportStatus('success');
      resetTimerRef.current = setTimeout(() => {
        setExportStatus('idle');
        resetTimerRef.current = null;
      }, 5000);
    } catch {
      setExportStatus('error');
    }
  };

  return (
    <>
      <button
        type="button"
        onClick={handleExport}
        disabled={exportStatus === 'loading'}
        aria-busy={exportStatus === 'loading'}
        aria-label="Export your data"
        className="w-full flex items-center gap-3 p-3 rounded-2xl hover:bg-[color-mix(in_oklab,var(--text-primary)_5%,transparent)] transition-all text-left focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--ring)] disabled:opacity-60 disabled:cursor-not-allowed"
      >
        <div className="w-10 h-10 rounded-full bg-[color-mix(in_oklab,var(--text-primary)_7%,transparent)] flex items-center justify-center shrink-0">
          {exportStatus === 'loading' ? (
            <span
              aria-hidden
              className="animate-spin rounded-full h-5 w-5 border-b-2 border-[var(--primary)]"
            />
          ) : (
            <Download className="w-5 h-5 text-[var(--text-secondary)]" aria-hidden />
          )}
        </div>
        <div className="min-w-0 flex-1">
          <p className="font-semibold text-[var(--text-primary)]">Export your data</p>
          <p className="text-xs text-[var(--text-tertiary)]">Download everything as Markdown</p>
        </div>
      </button>

      {exportStatus === 'error' ? (
        <p role="alert" className="mt-1 px-3 text-xs text-[var(--text-primary)]">
          {"That export didn't finish. Please try again."}
        </p>
      ) : (
        <p
          role="status"
          aria-live="polite"
          className="mt-1 px-3 flex items-center gap-2 text-xs text-[var(--text-tertiary)]"
        >
          {exportStatus === 'loading' && (
            <span
              aria-hidden
              className="animate-spin rounded-full h-5 w-5 border-b-2 border-[var(--primary)]"
            />
          )}
          {exportStatus === 'loading'
            ? 'Preparing your export…'
            : exportStatus === 'success'
              ? 'Export ready — check your downloads.'
              : ''}
        </p>
      )}
    </>
  );
}
